import React, { useState, useEffect, useRef } from 'react';
import { ChatCircleDots, X, PaperPlaneTilt, CircleNotch, Trash, User, Phone, Clock, CheckCircle, WhatsappLogo, EnvelopeSimple, CaretRight } from '@phosphor-icons/react';
import { motion, AnimatePresence } from '@/lib/motion';
import axios from 'axios';
import { useWhatsAppNumber, getWhatsAppUrl } from '../hooks/useWhatsAppNumber';
import { AIAssistantAvatar } from './AIAssistantAvatar';

/**
 * Minimal safe markdown renderer for chat messages. Handles two Aria-common
 * patterns without introducing a heavy dependency:
 *   - `[label](url)` → real clickable <a>
 *   - `**bold**`    → <strong>
 * Everything else is rendered verbatim as text — no HTML injection possible
 * because we split on regex matches, escape untouched text, and only build
 * safe React elements.
 */
const MD_LINK = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|wa\.me\/[^\s)]+|mailto:[^\s)]+|tel:[^\s)]+|\/[^\s)]*)\)/g;
const MD_BOLD = /\*\*([^*]+)\*\*/g;
function renderInline(text) {
  const nodes = [];
  let cursor = 0;
  // Collect all matches (links + bold), sort by position, then splice.
  const matches = [];
  let m;
  while ((m = MD_LINK.exec(text)) !== null) {
    matches.push({ start: m.index, end: MD_LINK.lastIndex, kind: 'link', label: m[1], url: m[2] });
  }
  while ((m = MD_BOLD.exec(text)) !== null) {
    matches.push({ start: m.index, end: MD_BOLD.lastIndex, kind: 'bold', label: m[1] });
  }
  matches.sort((a, b) => a.start - b.start);
  let key = 0;
  for (const match of matches) {
    if (match.start < cursor) continue; // overlap — skip
    if (match.start > cursor) nodes.push(text.slice(cursor, match.start));
    if (match.kind === 'link') {
      const url = match.url.startsWith('wa.me') ? `https://${match.url}` : match.url;
      nodes.push(
        <a
          key={`md-${key++}`}
          href={url}
          target={url.startsWith('http') ? '_blank' : undefined}
          rel="noopener noreferrer"
          className="underline text-bronze hover:text-obsidian font-semibold"
        >
          {match.label}
        </a>
      );
    } else {
      nodes.push(<strong key={`md-${key++}`}>{match.label}</strong>);
    }
    cursor = match.end;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));
  return nodes.length ? nodes : text;
}

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// UAE Business Hours (Sunday-Thursday, 8:00 AM - 6:00 PM GST)
const getBusinessStatus = () => {
  const now = new Date();
  // Convert to UAE time (UTC+4)
  const uaeTime = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
  const day = uaeTime.getDay(); // 0 = Sunday, 6 = Saturday
  const hour = uaeTime.getHours();
  
  // UAE work days: Sunday (0) to Thursday (4)
  const isWorkDay = day >= 0 && day <= 4;
  // Work hours: 8 AM to 6 PM
  const isWorkHours = hour >= 8 && hour < 18;
  
  if (isWorkDay && isWorkHours) {
    return { isOnline: true, status: 'Online', message: 'Team available until 6:00 PM' };
  } else if (isWorkDay && hour < 8) {
    return { isOnline: false, status: 'Away', message: 'Opens at 8:00 AM today' };
  } else if (day === 5) { // Friday
    return { isOnline: false, status: 'Away', message: 'Opens Sunday 8:00 AM' };
  } else if (day === 6) { // Saturday
    return { isOnline: false, status: 'Away', message: 'Opens Sunday 8:00 AM' };
  } else {
    return { isOnline: false, status: 'Away', message: 'Opens tomorrow 8:00 AM' };
  }
};

// Quick action suggestions
const quickActions = [
  { label: 'Get a Quote', action: 'quote' },
  { label: 'Request Samples', action: 'samples' },
  { label: 'Book Consultation', action: 'booking' },
  { label: 'Technical Question', action: 'technical' },
];

// Quick starter questions with icons
const starterQuestions = [
  { icon: '💰', text: 'Get pricing estimate', query: 'How much does architectural metalwork typically cost?' },
  { icon: '🏗️', text: 'See notable projects', query: 'Show me your notable projects and case studies' },
  { icon: '✨', text: 'Explore finishes', query: 'What metal finishes do you offer?' },
  { icon: '⏱️', text: 'Lead times', query: 'What are your typical lead times?' },
  { icon: '📋', text: 'Request a quote', action: '/quote' },
  { icon: '📅', text: 'Book a visit', action: '/booking' },
];

export function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [showHumanForm, setShowHumanForm] = useState(false);
  const [humanFormData, setHumanFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [humanFormSubmitted, setHumanFormSubmitted] = useState(false);
  const [submittingHuman, setSubmittingHuman] = useState(false);
  const [businessStatus, setBusinessStatus] = useState(getBusinessStatus());
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('ihgind_chat_session');
    return stored || `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  });
  const messagesEndRef = useRef(null);
  const whatsappNumber = useWhatsAppNumber();

  // Update business status every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setBusinessStatus(getBusinessStatus());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem('ihgind_chat_session', sessionId);
  }, [sessionId]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadHistory();
    }
  }, [isOpen]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadHistory = async () => {
    try {
      const res = await axios.get(`${API}/chat/history/${sessionId}`);
      if (res.data.messages?.length > 0) {
        setMessages(res.data.messages);
      } else {
        setMessages([{
          role: 'assistant',
          content: "Hi, I'm Aria — IHGIND's AI assistant. I can walk you through our capabilities, finishes, projects, pricing, lead times, or set up a consultation.\n\nWhat brings you in today?",
          showQuickActions: true
        }]);
      }
    } catch (e) {
      console.error('Failed to load chat history:', e);
      setMessages([{
        role: 'assistant',
        content: "Hi, I'm Aria — IHGIND's AI assistant. How can I help you today?",
        showQuickActions: true
      }]);
    }
  };

  const handleQuickAction = (action) => {
    const actionMessages = {
      quote: "I'd like to get a quote for my project",
      samples: "I'm interested in requesting material samples",
      booking: "I'd like to book a consultation with your team",
      technical: "I have a technical question about your capabilities"
    };
    setInput(actionMessages[action]);
  };

  const handleStarterClick = (starter) => {
    if (starter.action) {
      window.location.href = starter.action;
    } else if (starter.query) {
      setInput('');
      setMessages(prev => [...prev, { role: 'user', content: starter.query }]);
      setLoading(true);
      
      axios.post(`${API}/chat`, {
        message: starter.query,
        session_id: sessionId
      }).then(res => {
        setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
        setLoading(false);
      }).catch(() => {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at sales@ihgind.com" 
        }]);
        setLoading(false);
      });
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const res = await axios.post(`${API}/chat`, {
        message: userMessage,
        session_id: sessionId
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (e) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble connecting right now. Please try again or contact us directly at sales@ihgind.com" 
      }]);
    }
    setLoading(false);
  };

  const clearChat = async () => {
    try {
      await axios.delete(`${API}/chat/history/${sessionId}`);
      setMessages([{
        role: 'assistant',
        content: "Chat cleared. How can I help you today?",
        showQuickActions: true
      }]);
      setShowHumanForm(false);
      setHumanFormSubmitted(false);
    } catch (e) {
      console.error('Failed to clear chat:', e);
    }
  };

  const submitHumanRequest = async (e) => {
    e.preventDefault();
    setSubmittingHuman(true);

    // Get conversation history for context
    const conversationSummary = messages
      .slice(-6)
      .map(m => `${m.role === 'user' ? 'Customer' : 'AI'}: ${m.content}`)
      .join('\n');

    try {
      await axios.post(`${API}/inquiries`, {
        name: humanFormData.name,
        email: humanFormData.email,
        phone: humanFormData.phone,
        company: '',
        message: `HUMAN SUPPORT REQUEST\n\nCustomer Message: ${humanFormData.message}\n\n--- Recent Chat History ---\n${conversationSummary}`
      });
      setHumanFormSubmitted(true);
      setMessages(prev => [...prev, {
        role: 'system',
        content: `Your request has been submitted! Our team will contact you at ${humanFormData.email} shortly.`
      }]);
    } catch (e) {
      alert('Failed to submit. Please try again or email sales@ihgind.com directly.');
    }
    setSubmittingHuman(false);
  };

  // Floating Button — shows Aria's avatar so users see the friendly AI face first.
  if (!isOpen) {
    return (
      <motion.button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-24 right-4 sm:bottom-24 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-obsidian text-white shadow-xl flex items-center justify-center z-50 group rounded-full overflow-hidden"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        aria-label={businessStatus.isOnline ? 'Chat with Aria — AI assistant' : 'Leave a message for Aria'}
        data-testid="chat-widget-btn"
      >
        <AIAssistantAvatar size={56} showHalo={false} className="w-full h-full" />
        {/* Online indicator */}
        <span
          className={`absolute bottom-1 right-1 w-3 h-3 rounded-full border-2 border-obsidian ${businessStatus.isOnline ? 'bg-green-500' : 'bg-amber-500'}`}
          aria-hidden="true"
        />
        <span className="hidden sm:block absolute -top-10 right-0 bg-obsidian text-white text-xs px-4 py-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap font-body rounded" aria-hidden="true">
          {businessStatus.isOnline ? 'Chat with Aria' : 'Leave Aria a message'}
        </span>
      </motion.button>
    );
  }

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 w-full sm:w-[340px] md:w-[360px] h-full sm:h-[480px] md:h-[520px] bg-white shadow-2xl flex flex-col z-50 sm:border sm:border-black/5 sm:rounded-lg"
        data-testid="chat-widget"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 sm:px-4 sm:py-3 bg-obsidian text-white flex-shrink-0 sm:rounded-t-lg">
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full overflow-hidden bg-white/5 flex items-center justify-center ring-2 ring-bronze/40">
                <AIAssistantAvatar size={44} showHalo={false} />
              </div>
              {/* Online indicator */}
              <span className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 sm:w-3 sm:h-3 rounded-full border-2 border-obsidian ${businessStatus.isOnline ? 'bg-green-500' : 'bg-amber-500'}`} />
            </div>
            <div>
              <h3 className="font-display text-xs sm:text-sm font-bold">Aria · IHGIND AI Assistant</h3>
              <div className="flex items-center gap-1.5">
                <span className={`w-1.5 h-1.5 rounded-full ${businessStatus.isOnline ? 'bg-green-400' : 'bg-amber-400'}`} />
                <p className="font-body text-[9px] sm:text-[10px] text-white/60">{businessStatus.status}</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            <button 
              onClick={clearChat} 
              className="p-2 hover:bg-white/10 transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center" 
              title="Clear chat"
              aria-label="Clear chat history"
              data-testid="chat-clear-btn"
            >
              <Trash weight="bold" className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setIsOpen(false)} 
              className="p-2 hover:bg-white/10 transition-colors min-w-[40px] min-h-[40px] sm:min-w-[44px] sm:min-h-[44px] flex items-center justify-center" 
              title="Close"
              aria-label="Close chat"
              data-testid="chat-close-btn"
            >
              <X weight="bold" className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-2.5 sm:space-y-3 bg-gallery">
          {messages.map((msg, i) => (
            <motion.div 
              key={i} 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[88%] sm:max-w-[85%] ${msg.role === 'system' ? 'w-full' : ''}`}>
                {msg.role === 'system' ? (
                  <div className="bg-green-50 border border-green-200 px-3 py-2.5 sm:px-4 sm:py-3 text-center">
                    <CheckCircle weight="fill" className="w-4 h-4 sm:w-5 sm:h-5 text-green-500 mx-auto mb-1" />
                    <p className="font-body text-xs sm:text-sm text-green-700">{msg.content}</p>
                  </div>
                ) : (
                  <div className={`px-3 py-2.5 sm:px-4 sm:py-3 font-body text-xs sm:text-sm leading-relaxed ${
                    msg.role === 'user' 
                      ? 'bg-obsidian text-white' 
                      : 'bg-white text-obsidian border border-black/5'
                  }`}>
                    <div className="whitespace-pre-wrap break-words">{msg.role === 'assistant' ? renderInline(msg.content) : msg.content}</div>
                  </div>
                )}
                
                {/* Quick Starter Questions - More compact on mobile */}
                {msg.showQuickActions && (
                  <div className="mt-2.5 sm:mt-3 space-y-1.5 sm:space-y-2">
                    <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider text-concrete mb-1.5 sm:mb-2">Quick Questions</p>
                    <div className="grid grid-cols-2 gap-1.5 sm:gap-2">
                      {starterQuestions.slice(0, 4).map((starter, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleStarterClick(starter)}
                          className="px-2 py-2 sm:px-3 sm:py-2.5 bg-white border border-black/10 text-[10px] sm:text-xs font-medium text-obsidian hover:border-bronze hover:bg-bronze/5 transition-colors text-left flex items-center gap-1.5 sm:gap-2 group"
                          data-testid={`starter-${idx}`}
                        >
                          <span className="text-xs sm:text-sm">{starter.icon}</span>
                          <span className="group-hover:text-bronze transition-colors truncate">{starter.text}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          ))}
          
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white px-3 py-2.5 sm:px-4 sm:py-3 border border-black/5">
                <CircleNotch weight="bold" className="w-4 h-4 animate-spin text-bronze" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Human Support Form */}
        <AnimatePresence>
          {showHumanForm && !humanFormSubmitted && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-white border-t border-black/10 overflow-hidden flex-shrink-0 max-h-[55vh] overflow-y-auto"
            >
              <form onSubmit={submitHumanRequest} className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                <div className="flex items-center justify-between mb-1.5 sm:mb-2">
                  <h4 className="font-display text-[10px] sm:text-xs font-bold uppercase">Talk to Our Team</h4>
                  <button 
                    type="button" 
                    onClick={() => setShowHumanForm(false)} 
                    className="text-concrete hover:text-obsidian min-w-[40px] min-h-[40px] flex items-center justify-center"
                    aria-label="Close form"
                  >
                    <X weight="bold" className="w-4 h-4" />
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Your Name *"
                  required
                  aria-label="Your name"
                  value={humanFormData.name}
                  onChange={(e) => setHumanFormData({...humanFormData, name: e.target.value})}
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-black/10 focus:border-obsidian focus:outline-none min-h-[40px] sm:min-h-[44px]"
                />
                <input
                  type="email"
                  placeholder="Email *"
                  required
                  aria-label="Email address"
                  value={humanFormData.email}
                  onChange={(e) => setHumanFormData({...humanFormData, email: e.target.value})}
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-black/10 focus:border-obsidian focus:outline-none min-h-[40px] sm:min-h-[44px]"
                />
                <input
                  type="tel"
                  placeholder="Phone (optional)"
                  aria-label="Phone number"
                  value={humanFormData.phone}
                  onChange={(e) => setHumanFormData({...humanFormData, phone: e.target.value})}
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-black/10 focus:border-obsidian focus:outline-none min-h-[40px] sm:min-h-[44px]"
                />
                <textarea
                  placeholder="How can we help? *"
                  required
                  aria-label="Your message"
                  rows={2}
                  value={humanFormData.message}
                  onChange={(e) => setHumanFormData({...humanFormData, message: e.target.value})}
                  className="w-full px-3 py-2 text-xs sm:text-sm border border-black/10 focus:border-obsidian focus:outline-none resize-none min-h-[50px] sm:min-h-[60px]"
                />
                <button
                  type="submit"
                  disabled={submittingHuman}
                  className="w-full py-2 sm:py-2.5 bg-bronze text-obsidian text-[10px] sm:text-xs font-semibold uppercase tracking-wider hover:bg-obsidian hover:text-white transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingHuman ? <CircleNotch weight="bold" className="w-4 h-4 animate-spin" /> : <User weight="bold" className="w-4 h-4" />}
                  {submittingHuman ? 'Submitting...' : 'Request Human Support'}
                </button>
              </form>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Input Area */}
        <div className="border-t border-black/5 bg-white flex-shrink-0 sm:rounded-b-lg">
          {/* Quick Contact Options - Compact on mobile */}
          <div className="flex items-center justify-between px-3 py-1.5 sm:px-4 sm:py-2 border-b border-black/5 bg-gallery/50">
            <button
              onClick={() => setShowHumanForm(!showHumanForm)}
              className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-[9px] sm:text-[10px] font-semibold uppercase tracking-wider transition-colors min-h-[36px] sm:min-h-[40px] ${
                showHumanForm 
                  ? 'bg-bronze text-obsidian' 
                  : 'bg-white border border-black/10 text-concrete hover:text-obsidian hover:border-obsidian'
              }`}
              aria-label="Request human support"
              data-testid="talk-to-human-btn"
            >
              <User weight="bold" className="w-3 h-3" />
              <span className="hidden xs:inline">Talk to</span> Human
            </button>
            <div className="flex items-center gap-1.5 sm:gap-2">
              <a
                href={getWhatsAppUrl(whatsappNumber)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Contact via WhatsApp"
                className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 bg-green-500 text-white text-[10px] font-semibold uppercase hover:bg-green-600 transition-colors min-w-[36px] sm:min-w-[44px] min-h-[36px] sm:min-h-[44px] justify-center"
                data-testid="whatsapp-btn"
              >
                <WhatsappLogo weight="fill" className="w-4 h-4" />
              </a>
              <a
                href="mailto:sales@ihgind.com"
                aria-label="Send email"
                className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 bg-obsidian text-white text-[10px] font-semibold uppercase hover:bg-bronze hover:text-obsidian transition-colors min-w-[36px] sm:min-w-[44px] min-h-[36px] sm:min-h-[44px] justify-center"
                data-testid="email-btn"
              >
                <EnvelopeSimple weight="bold" className="w-4 h-4" />
              </a>
              <a
                href="tel:+97142585754"
                aria-label="Call us"
                className="flex items-center px-2 sm:px-3 py-1.5 sm:py-2 bg-obsidian text-white text-[10px] font-semibold uppercase hover:bg-bronze hover:text-obsidian transition-colors min-w-[36px] sm:min-w-[44px] min-h-[36px] sm:min-h-[44px] justify-center"
                data-testid="phone-btn"
              >
                <Phone weight="bold" className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Message Input */}
          <form onSubmit={sendMessage} className="p-2.5 sm:p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type your message..."
                aria-label="Chat message"
                className="flex-1 px-3 sm:px-4 py-2 sm:py-2.5 font-body text-xs sm:text-sm border border-black/10 focus:border-obsidian focus:outline-none transition-colors bg-gallery min-h-[40px] sm:min-h-[44px]"
                disabled={loading}
                data-testid="chat-input"
              />
              <button
                type="submit"
                disabled={loading || !input.trim()}
                aria-label="Send message"
                className="px-3 sm:px-4 py-2 sm:py-2.5 bg-obsidian text-white hover:bg-bronze hover:text-obsidian transition-colors disabled:opacity-40 disabled:cursor-not-allowed min-w-[40px] sm:min-w-[44px] min-h-[40px] sm:min-h-[44px]"
                data-testid="chat-send-btn"
              >
                <PaperPlaneTilt weight="fill" className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
